import React, { useEffect, useState } from 'react';
import { GraduationCap, ImagePlus, Star, ChevronLeft, ChevronRight, Award } from 'lucide-react';
import { contactEmails, iceLogo, heroImages, youngScholars, awardScans } from '../mock';
import { listScholars } from '../lib/api';

export default function Scholars() {
  const [scholars, setScholars] = useState(youngScholars);

  useEffect(() => {
    listScholars()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));
          setScholars(sorted);
        }
      })
      .catch(() => {
        // fall back to mock — no-op
      });
  }, []);

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-20 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[1]} alt="stadium" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44]/70 to-[#0a1f44]" />
          <div className="absolute inset-0 diagonal-stripes opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <img src={iceLogo} alt="ICE Logo" className="h-14 w-auto object-contain" data-testid="scholars-hero-logo" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs">Next-Gen Patriots</div>
          </div>
          <h1 className="font-display text-white text-6xl md:text-8xl leading-none" data-testid="scholars-title">
            YOUNG <span className="text-[#c8102e]">SCHOLARS</span>
          </h1>
          <p className="text-white/75 max-w-2xl mt-6 text-lg">
            Celebrating our young scholar-athletes who represent the future of the &quot;ICE&quot; Brookline JP Patriots — on the field, on the mat, and in the classroom.
          </p>
        </div>
      </section>

      {/* 2x2 Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <GraduationCap className="w-10 h-10 text-[#c8102e] mx-auto mb-4" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Spotlight</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">SCHOLAR OF THE SEASON</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="scholars-grid">
            {scholars.map((s, i) => (
              <div key={i} data-testid={`scholar-card-${i}`} className="hover-lift group">
                <div className="relative">
                  <div className="absolute -top-3 -left-3 w-full h-full border-4 border-[#c8102e]" />
                  <div className="relative aspect-square bg-[#0a1f44] flex items-center justify-center overflow-hidden">
                    {s.featured && (
                      <div data-testid={`scholar-featured-badge-${i}`} className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 bg-[#c8102e] text-white px-3 py-1.5 font-heading uppercase tracking-widest text-[10px]">
                        <Star className="w-3 h-3 fill-white" /> All-American
                      </div>
                    )}
                    {s.img ? (
                      <img
                        src={s.img}
                        alt={s.name}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: s.focus || 'center top' }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-white/40">
                        <ImagePlus className="w-12 h-12 mb-3" />
                        <div className="font-heading uppercase tracking-[0.2em] text-xs">Photo coming soon</div>
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0a1f44] to-transparent p-6">
                      <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs">{s.year}</div>
                      <div className="font-display text-white text-3xl leading-tight mt-1" data-testid={`scholar-name-${i}`}>{s.name}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Slot 4 — Certificate slideshow */}
            <CertificateSlideshow scans={awardScans} />
          </div>

          <p className="text-center text-[#0a1f44]/60 text-sm mt-12 max-w-xl mx-auto">
            Know a young Patriot who deserves the spotlight? Email{' '}
            {contactEmails.map((email, index) => (
              <React.Fragment key={email}>
                {index > 0 && ' or '}
                <a href={`mailto:${email}`} data-testid={`scholars-contact-email-${index + 1}`} className="text-[#c8102e] hover:underline break-all">{email}</a>
              </React.Fragment>
            ))}{' '}
            with their name, year, and photo.
          </p>
        </div>
      </section>
    </div>
  );
}


function CertificateSlideshow({ scans }) {
  const hasScans = Array.isArray(scans) && scans.length > 0;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!hasScans || scans.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % scans.length), 4000);
    return () => clearInterval(id);
  }, [hasScans, scans]);

  const prev = () => setIdx((i) => (i - 1 + scans.length) % scans.length);
  const next = () => setIdx((i) => (i + 1) % scans.length);

  return (
    <div data-testid="award-slideshow" className="hover-lift group">
      <div className="relative">
        <div className="absolute -top-3 -left-3 w-full h-full border-4 border-[#c8102e]" />
        <div className="relative aspect-square bg-[#0a1f44] flex items-center justify-center overflow-hidden">
          <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 bg-[#c8102e] text-white px-3 py-1.5 font-heading uppercase tracking-widest text-[10px]">
            <Award className="w-3 h-3" /> Wall of Honor
          </div>

          {hasScans ? (
            <>
              {scans.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Award certificate ${i + 1}`}
                  className={`absolute inset-0 w-full h-full object-contain bg-white transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
                  style={{ transform: 'rotate(-90deg) scale(1.35)' }}
                />
              ))}
              {scans.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    data-testid="award-slideshow-prev"
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-[#0a1f44]/70 hover:bg-[#c8102e] text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Previous"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={next}
                    data-testid="award-slideshow-next"
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-[#0a1f44]/70 hover:bg-[#c8102e] text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="absolute top-3 left-3 z-10 bg-[#0a1f44]/70 text-white font-heading uppercase tracking-widest text-[10px] px-2 py-1">
                    {idx + 1} / {scans.length}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center text-white/40 px-6 text-center">
              <Award className="w-12 h-12 mb-3" />
              <div className="font-heading uppercase tracking-[0.2em] text-xs">Certificates coming soon</div>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0a1f44] to-transparent p-6 pointer-events-none z-[5]">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs">Award Certificates</div>
            <div className="font-display text-white text-3xl leading-tight mt-1">WALL OF HONOR</div>
          </div>
        </div>
      </div>
    </div>
  );
}
